import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { LibraryView } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import { Divider } from "js/Components/Divider";
import UpgradeIcon from "js/Components/UpgradeIcon";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import {
    navigationSelectedClass,
    StyledNavigationIcon,
    StyledNavigationSideBarItem,
} from "js/editor/PresentationLibrary/libraryNavigationItems";
import { LibraryTeamSettingsNavigation } from "js/editor/PresentationLibrary/LibraryTeamSettingsNavigation";
import { t } from "js/i18n";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResourceTypes";

const TEAM_LIBRARIES_LABEL = "Team Resources";

const NavigationSectionLabel = styled.div`
    color: #ccc;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    padding: 10px;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`;

const ExpandIcon = styled(StyledNavigationIcon)<{ $expanded: boolean }>`
    &&& {
        color: #DDD;
        font-size: 20px;
        transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "-90deg")});
        transition: transform 160ms ease;
    }
`;

const TeamLibrariesLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const TeamResourcesSection = styled.div<{ $expanded: boolean }>`
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: opacity 160ms ease;
`;

const TeamResourcesSectionContent = styled.div`
    min-height: 0;
    overflow: hidden;
`;

type TeamLibrariesNavItemsProps = {
    teamPane?: string;
};

function TeamLibrariesNavItems({ teamPane }: TeamLibrariesNavItemsProps) {
    return (
        <>
            <StyledNavigationSideBarItem
                className={navigationSelectedClass(teamPane === TeamResourceTypes.TEAM_THEMES)}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_THEMES })}
                data-test-id="sidebar-nav-team-themes"
            >
                <StyledNavigationIcon>color_lens</StyledNavigationIcon>
                {t("Team Themes")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(teamPane === TeamResourceTypes.PRESENTATION_TEMPLATES)}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.PRESENTATION_TEMPLATES })}
                data-test-id="sidebar-nav-team-templates"
            >
                <StyledNavigationIcon>video_library</StyledNavigationIcon>
                {t("Team Templates")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(
                    teamPane === TeamResourceTypes.SLIDE_TEMPLATES ||
                    teamPane === TeamResourceTypes.SHARED_SLIDES ||
                    teamPane === TeamResourceTypes.SLIDES
                )}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.SLIDES })}
                data-test-id="sidebar-nav-team-slides"
            >
                <StyledNavigationIcon>library_add</StyledNavigationIcon>
                {t("Team Slides")}
            </StyledNavigationSideBarItem>

            <StyledNavigationSideBarItem
                className={navigationSelectedClass(
                    teamPane === TeamResourceTypes.TEAM_ASSETS ||
                    teamPane === TeamResourceTypes.TEAM_ASSETS_IMAGES ||
                    teamPane === TeamResourceTypes.SHARED_ASSETS_LOGOS
                )}
                onClick={() => AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_ASSETS })}
                data-test-id="sidebar-nav-team-assets"
            >
                <StyledNavigationIcon>photo_library</StyledNavigationIcon>
                {t("Team Assets")}
            </StyledNavigationSideBarItem>

            <LibraryTeamSettingsNavigation teamPane={teamPane} />
        </>
    );
}

type TeamLibrariesNavigationProps = {
    teamPane?: string;
};

/** Team plan: shared resources already available — always expanded, no upgrade CTA. */
function TeamLibrariesNavigation({ teamPane }: TeamLibrariesNavigationProps) {
    return (
        <>
            <Divider margin={0} style={{ marginTop: 5 }} color="#444" />
            <NavigationSectionLabel data-test-id="sidebar-nav-team-libraries-header">
                <TeamLibrariesLabel>
                    <span>{t(TEAM_LIBRARIES_LABEL)}</span>
                </TeamLibrariesLabel>
            </NavigationSectionLabel>
            <TeamLibrariesNavItems teamPane={teamPane} />
        </>
    );
}

type ProTeamLibrariesNavigationProps = {
    libraryView: typeof LibraryView.LIBRARY | typeof LibraryView.TEMPLATES | typeof LibraryView.TEAM_RESOURCES;
    teamPane?: string;
};

/** Pro plan: can upgrade to Team — collapsible section + upgrade icon. */
function ProTeamLibrariesNavigation({ libraryView, teamPane }: ProTeamLibrariesNavigationProps) {
    const [isExpanded, setIsExpanded] = useState(libraryView === LibraryView.TEAM_RESOURCES);

    useEffect(() => {
        if (libraryView === LibraryView.TEAM_RESOURCES) {
            setIsExpanded(true);
        }
    }, [libraryView]);

    const showUpgradeDialog = () => {
        ShowDialog(ChangePlanDialog, {
            featuredPlan: WorkspacePlanTier.TEAM
        });
    };

    const onHeaderClick = () => {
        if (!isExpanded) {
            setIsExpanded(true);
            AppController.showTeamResources({ pane: TeamResourceTypes.TEAM_THEMES });
            return;
        }

        setIsExpanded(false);
        AppController.showLibrary();
    };

    return (
        <>
            <Divider margin={0} style={{ marginTop: 5 }} color="#444" />
            <NavigationSectionLabel
                data-test-id="sidebar-nav-team-libraries-header"
                onClick={onHeaderClick}
            >
                <TeamLibrariesLabel>
                    <ExpandIcon $expanded={isExpanded}>
                        expand_more
                    </ExpandIcon>
                    <span>{t(TEAM_LIBRARIES_LABEL)}</span>
                </TeamLibrariesLabel>
                <UpgradeIcon
                    onClick={event => {
                        event.stopPropagation();
                        showUpgradeDialog();
                    }}
                />
            </NavigationSectionLabel>
            <TeamResourcesSection $expanded={isExpanded}>
                <TeamResourcesSectionContent>
                    <TeamLibrariesNavItems teamPane={teamPane} />
                </TeamResourcesSectionContent>
            </TeamResourcesSection>
        </>
    );
}

type LibraryTeamLibrariesNavigationProps = {
    libraryView: typeof LibraryView.LIBRARY | typeof LibraryView.TEMPLATES | typeof LibraryView.TEAM_RESOURCES;
    teamPane?: string;
};

export function LibraryTeamLibrariesNavigation({ libraryView, teamPane }: LibraryTeamLibrariesNavigationProps) {
    const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.USE
    );

    if (canUpgradeToUseSharedResources) {
        return <ProTeamLibrariesNavigation libraryView={libraryView} teamPane={teamPane} />;
    }

    return <TeamLibrariesNavigation teamPane={teamPane} />;
}
